import { h } from 'vue';

import {
  renderBoolean,
  renderDateTime,
  renderMoney,
  titleCase,
  translateEnum,
  translateNumberedEntity,
} from '@rygen/rygen-ui/i18n';
import {
  ColumnSpecification,
  Coordinates,
  Currency,
  Money,
  Optional,
  WeightUom,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';
import { Shipment as ESShipment } from '@rygen/tms-search-model/search/shipment';
import { ShipmentStop as EsShipmentStop } from '@rygen/tms-search-model/search/shipmentStop';

import i18n from '@/i18n';
import { buildSearchRoute, formatDateRange } from '@/util';

import CarrierAvatarAndName from '@/components/carrier/CarrierAvatarAndName.vue';
import ManifestStatusChip from '@/components/manifest/ManifestStatusChip.vue';
import ClientSearchResult from '@/components/search/ClientSearchResult.vue';
import ShippingPointSearchResult from '@/components/search/ShippingPointSearchResult.vue';
import ShipmentChip from '@/components/shipment/ShipmentChip.vue';
import AssignedUsersSearchResult from '@/components/user/AssignedUsersSearchResult.vue';
import ShipmentFinancialStatusChip from '@rygen/rygen-ui/components/shipment/financials/ShipmentFinancialStatusChip.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import Chip from 'primevue/chip';

import { EmbeddableLocation } from '../EmbeddableLocation';
import { ManifestStatus } from '../shipment/manifest/ManifestStatus';
import { ShipmentFinancialStatus } from '../shipment/ShipmentFinancialStatus';
import { ShipmentHealth } from '../shipment/ShipmentHealth';
import { ShipmentStatus } from '../shipment/ShipmentStatus';
import { ShipmentStop } from '../shipment/ShipmentStop';
import { ShipmentStopType } from '../shipment/ShipmentStopType';

import {
  ES_DESTINATION_LOCATION_GROUP_COLUMN,
  ES_ORIGIN_LOCATION_GROUP_COLUMN,
} from './locationGroupColumn';
import { EsShipmentColumnProps } from './ShipmentColumnProps';

const convertEsShipmentStop = (
  stop: Optional<EsShipmentStop>,
  stopType: ShipmentStopType,
) => {
  return stop
    ? new ShipmentStop({
        id: stop.id,
        stopType,
        location: new EmbeddableLocation({
          locationId: stop.location?.ficonLocationId,
          locationName: stop.location?.locationName,
          code: stop.location?.code,
          addressLine1: stop.location?.addressLine1,
          addressLine2: stop.location?.addressLine2,
          addressLine3: stop.location?.addressLine3,
          city: stop.location?.city,
          state: stop.location?.state,
          postalCode: stop.location?.postalCode,
          country: stop.location?.country,
          coordinates: new Coordinates({
            latitude: stop.location?.coordinates?.lat,
            longitude: stop.location?.coordinates?.lon,
          }),
          timeZone: stop.location?.timeZone,
        }),
      })
    : undefined;
};

const tagSearchRouteBuilder = (tag: string) => {
  return buildSearchRoute('shipmentList', { tags: [tag] });
};

const OWNING_CLIENT_COLUMN = new ColumnSpecification({
  field: 'client',
  header: i18n.global.t('clients.clients'),
  body: ({ data }: EsShipmentColumnProps) =>
    h(ClientSearchResult, { domainId: data.owner?.id }),
});

const STATUS_HEALTH_COLUMN = new ColumnSpecification({
  field: 'status',
  header: `${i18n.global.t('common.status')} / ${i18n.global.t(
    'shipments.health',
  )}`,
  body: ({ data }: EsShipmentColumnProps) => {
    return h(ShipmentChip, {
      status: data.status as ShipmentStatus,
      health: data.health as ShipmentHealth,
    });
  },
});

const FINANCIAL_STATUS_COLUMN = new ColumnSpecification({
  field: 'financialStatus',
  header: i18n.global.t('common.paramStatuses', {
    param: i18n.global.t('shipments.financials.financials'),
  }),
  body: ({ data }: EsShipmentColumnProps) => {
    return data.financialSummary?.financialStatus
      ? h(ShipmentFinancialStatusChip, {
          status: data.financialSummary
            .financialStatus as ShipmentFinancialStatus,
        })
      : undefined;
  },
});

const TAG_COLUMN = new ColumnSpecification({
  field: 'tags',
  header: i18n.global.t('tags.tags', 2),
  class: 'tags-column',
  body: ({ data }: EsShipmentColumnProps) =>
    h(TagContainer, {
      class: 'h-auto',
      modelValue: data.tags ?? [],
      routeBuilder: tagSearchRouteBuilder,
      readonly: true,
      maxVisible: 2,
    }),
});

const CARRIER_COLUMN = new ColumnSpecification({
  field: 'carrierName',
  header: i18n.global.t('carriers.carriers'),
  body: ({ data }: EsShipmentColumnProps) => {
    return h(CarrierAvatarAndName, {
      name: data.carrier?.name,
      scac: data.carrier?.scac,
    });
  },
});

const MANIFEST_COLUMN = new ColumnSpecification({
  field: 'manifest',
  header: i18n.global.t('manifests.manifests', 1),
  body: ({ data }: EsShipmentColumnProps) => {
    if (!data.manifest?.manifestNumber) {
      return '-';
    }
    return h(
      'div',
      { class: 'flex flex-column flex-gap-1 align-items-start' },
      [
        h('span', data.manifest.manifestNumber),
        data.manifest.status
          ? h(ManifestStatusChip, {
              status: data.manifest.status as ManifestStatus,
            })
          : undefined,
      ],
    );
  },
});

export const ES_COST_ESTIMATE_COLUMN = new ColumnSpecification({
  field: 'costEstimate',
  header: i18n.global.t('shipmentFinancials.paramEstimate', {
    param: i18n.global.t('shipmentFinancials.cost'),
  }),
  body: ({ data }: EsShipmentColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const costEstimate = data.financialSummary?.costEstimate
      ? new Money({
          amount: data.financialSummary.costEstimate.amount,
          currency: data.financialSummary.costEstimate.currency as Currency,
        })
      : undefined;
    return renderMoney(costEstimate);
  },
});

const ORIGIN_COLUMN = new ColumnSpecification({
  field: 'origin',
  header: i18n.global.t('shipments.origin'),
  body: ({ data }: EsShipmentColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const shippingPoint = convertEsShipmentStop(
      data.origin,
      ShipmentStopType.PICKUP,
    );
    return h(ShippingPointSearchResult, { shippingPoint });
  },
});

const DESTINATION_COLUMN = new ColumnSpecification({
  field: 'destination',
  header: i18n.global.t('shipments.destinations'),
  body: ({ data }: EsShipmentColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const shippingPoint = convertEsShipmentStop(
      data.destination,
      ShipmentStopType.DROP_OFF,
    );
    return h(ShippingPointSearchResult, { shippingPoint });
  },
});

const STOP_COUNT_COLUMN = new ColumnSpecification({
  field: 'stopCount',
  header: i18n.global.t('shipments.stopCount'),
  body: ({ data }: EsShipmentColumnProps) => data.stopCount ?? '-',
});

export const ES_SERVICE_COLUMN = new ColumnSpecification({
  field: 'service',
  header: i18n.global.t('shipments.services'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.service ? titleCase(data.service) : '-',
});

const MODE_COLUMN = new ColumnSpecification({
  field: 'mode',
  header: i18n.global.t('shipments.modes'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.mode ? translateEnum(data.mode, 'Mode') : '-',
});

export const ES_REQUESTED_PICKUP_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedPickupDateRange',
  header: i18n.global.t('shipments.dates.requestedPickup'),
  body: ({ data }: EsShipmentColumnProps) =>
    formatDateRange(
      data.origin?.requestedAppointmentRange,
      data.origin?.location?.timeZone,
    ),
});

const REQUESTED_DELIVERY_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedDeliveryDateRange',
  header: i18n.global.t('shipments.dates.requestedDelivery'),
  body: ({ data }: EsShipmentColumnProps) =>
    formatDateRange(
      data.destination?.requestedAppointmentRange,
      data.destination?.location?.timeZone,
    ),
});

const SCHEDULED_PICKUP_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'scheduledPickupDateRange',
  header: i18n.global.t('shipments.dates.scheduledPickup'),
  body: ({ data }: EsShipmentColumnProps) =>
    formatDateRange(
      data.origin?.scheduledAppointmentRange,
      data.origin?.location?.timeZone,
    ),
});

const SCHEDULED_DELIVERY_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'scheduledDeliveryDateRange',
  header: i18n.global.t('shipments.dates.scheduledDelivery'),
  body: ({ data }: EsShipmentColumnProps) =>
    formatDateRange(
      data.destination?.scheduledAppointmentRange,
      data.destination?.location?.timeZone,
    ),
});

const EXPECTED_PICKUP_ARRIVAL_COLUMN = new ColumnSpecification({
  field: 'expectedPickupArrival',
  header: i18n.global.t('shipments.dates.expectedPickupArrival'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderDateTime(data.origin?.expectedArrival, {
      timeZone: data.origin?.location?.timeZone,
    }),
});

const ACTUAL_PICKUP_ARRIVAL_COLUMN = new ColumnSpecification({
  alias: ['actualPickup'],
  field: 'actualPickupArrival',
  header: i18n.global.t('shipments.dates.actualPickupArrival'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderDateTime(data.origin?.actualArrival, {
      timeZone: data.origin?.location?.timeZone,
    }),
});

const ACTUAL_PICKUP_DEPARTURE_COLUMN = new ColumnSpecification({
  alias: ['actualPickup'],
  field: 'actualPickupDeparture',
  header: i18n.global.t('shipments.dates.actualPickupDeparture'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderDateTime(data.origin?.actualDeparture, {
      timeZone: data.origin?.location?.timeZone,
    }),
});

const PICKUP_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'pickupAppointmentRequired',
  header: i18n.global.t('shippingPoints.pickupAppointmentRequired'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderBoolean(data.origin?.appointmentRequired ?? false),
});

const EXPECTED_DELIVERY_ARRIVAL_COLUMN = new ColumnSpecification({
  field: 'expectedDeliveryArrival',
  header: i18n.global.t('shipments.dates.expectedDeliveryArrival'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderDateTime(data.destination?.expectedArrival, {
      timeZone: data.destination?.location?.timeZone,
    }),
});

const ACTUAL_DELIVERY_ARRIVAL_COLUMN = new ColumnSpecification({
  alias: ['actualDelivery'],
  field: 'actualDeliveryArrival',
  header: i18n.global.t('shipments.dates.actualDeliveryArrival'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderDateTime(data.destination?.actualArrival, {
      timeZone: data.destination?.location?.timeZone,
    }),
});

const ACTUAL_DELIVERY_DEPARTURE_COLUMN = new ColumnSpecification({
  alias: ['actualDelivery'],
  field: 'actualDeliveryDeparture',
  header: i18n.global.t('shipments.dates.actualDeliveryDeparture'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderDateTime(data.destination?.actualDeparture, {
      timeZone: data.destination?.location?.timeZone,
    }),
});

const DELIVERY_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'deliveryAppointmentRequired',
  header: i18n.global.t('shippingPoints.deliveryAppointmentRequired'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderBoolean(data.destination?.appointmentRequired ?? false),
});

export const ES_WEIGHT_COLUMN = new ColumnSpecification({
  field: 'weight',
  header: i18n.global.t('lineItems.weight'),
  body: ({ data }: EsShipmentColumnProps) => {
    return data.lineItemSummary?.totalWeight?.value == null
      ? '-'
      : translateNumberedEntity(
          data.lineItemSummary.totalWeight.value,
          `uoms.weight.abbreviations.${data.lineItemSummary?.totalWeight?.unit ?? WeightUom.POUND}`,
        );
  },
});

const HANDLING_UNIT_COUNT = new ColumnSpecification({
  field: 'handlingUnitCount',
  header: i18n.global.t('lineItems.handlingUnits'),
  body: ({ data }: EsShipmentColumnProps) => {
    const qty = data.lineItemSummary?.totalHandlingQuantity;
    const uom =
      data.lineItemSummary?.handlingUnit?.name ??
      i18n.global.t('lineItems.handlingUnits');
    return qty == null ? '-' : `${qty} ${uom}`;
  },
});

const PRODUCT_NUMBER_COLUMN = new ColumnSpecification({
  field: 'productNumber',
  header: i18n.global.t('lineItems.productNumber'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.lineItemSummary?.productNumbers ?? '-',
});

const PRODUCT_DESCRIPTION_COLUMN = new ColumnSpecification({
  alias: ['productionDescription'],
  field: 'productDescription',
  header: i18n.global.t('lineItems.productDescription'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.lineItemSummary?.productDescriptions ?? '-',
});

const HOT_COLUMN = new ColumnSpecification({
  field: 'hot',
  header: i18n.global.t('common.hot'),
  body: ({ data }: EsShipmentColumnProps) => renderBoolean(data.hot ?? false),
});

const HAZMAT_COLUMN = new ColumnSpecification({
  field: 'hazmat',
  header: i18n.global.t('lineItems.hazmat'),
  body: ({ data }: EsShipmentColumnProps) => {
    const lineItems = data.lineItems ?? [];
    const isHazmat = lineItems.some((l) => l.hazmatData?.hazmat);
    return renderBoolean(isHazmat);
  },
});

const PAYABLE_COLUMN = new ColumnSpecification({
  field: 'payable',
  header: i18n.global.t('shipments.payable'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderBoolean(data.financialSummary?.payable ?? false),
});

const GL_CODE_COLUMN = new ColumnSpecification({
  field: 'glCodes',
  header: i18n.global.t('glCodes.glCodes', 2),
  class: 'gl-codes-column',
  body: ({ data }: EsShipmentColumnProps) => {
    if (!data.glCodes?.length) {
      return '-';
    }

    const glCodes = data.glCodes.map((g) => h(Chip, { label: g }));
    return h('div', { class: 'flex flex-gap-1' }, glCodes);
  },
});

const DIRECTION_COLUMN = new ColumnSpecification({
  field: 'direction',
  header: i18n.global.t('shipments.directions'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.direction ? translateEnum(data.direction, 'Direction') : '-',
});

const BILLING_TERMS_COLUMN = new ColumnSpecification({
  field: 'billingTerms',
  header: i18n.global.t('shipments.billingTerms'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.billingTerms ? translateEnum(data.billingTerms, 'BillingTerms') : '-',
});

const EQUIPMENT_TYPE_COLUMN = new ColumnSpecification({
  field: 'equipmentType',
  header: i18n.global.t('carriers.equipmentTypes'),
  body: ({ data }: EsShipmentColumnProps) => data.equipmentType?.name ?? '-',
});

const FREIGHT_ALLOWANCE_ENABLED_COLUMN = new ColumnSpecification({
  field: 'freightAllowanceEnabled',
  header: i18n.global.t('orders.freightAllowance.freightAllowanceEnabled'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderBoolean(data.freightAllowance?.enabled ?? false),
});

const FREIGHT_ALLOWANCE_AMOUNT_COLUMN = new ColumnSpecification({
  field: 'freightAllowance',
  header: i18n.global.t('orders.freightAllowance.freightAllowance'),
  body: ({ data }: EsShipmentColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const freightAllowance = data.freightAllowance?.amount
      ? new Money({
          amount: data.freightAllowance.amount.amount,
          currency: data.freightAllowance.amount.currency as Currency,
        })
      : undefined;
    return renderMoney(freightAllowance);
  },
});

const SPOT_MARKET_SUMMARY_COLUMN = new ColumnSpecification({
  field: 'spotMarketRequestCount',
  header: i18n.global.t('carrierPortal.spotMarket.spotMarkets', 2),
  body: ({ data }: EsShipmentColumnProps) => {
    const spotMarketCountText = i18n.global.t(
      'carrierPortal.spotMarket.countSpotMarketRequestsSent',
      { count: data.spotMarketSummary?.requested ?? 0 },
    );

    if (!data.spotMarketSummary?.requested) {
      return spotMarketCountText;
    }

    const requestEl = h('div', {}, spotMarketCountText);
    const acceptedEl = h(
      'div',
      { class: 'text-gray-darker' },
      i18n.global.t(
        'carrierPortal.spotMarket.countSpotMarketRequestsAccepted',
        { count: data.spotMarketSummary.accepted ?? 0 },
      ),
    );
    return h('div', {}, [requestEl, acceptedEl]);
  },
});

const USER_ASSIGNMENT_COLUMN = new ColumnSpecification({
  field: 'assignedUsers',
  header: i18n.global.t('common.assignedParam', {
    param: i18n.global.t('users.users', 2),
  }),
  body: ({ data }: EsShipmentColumnProps) =>
    h(AssignedUsersSearchResult, {
      id: 'assign-user-button',
      modelValue: data.assignedUsers || [],
      parentId: data.id,
      ownerId: data.owner?.id,
      type: 'shipment',
    }),
});

const CANCELLATION_REASON_COLUMN = new ColumnSpecification({
  field: 'cancellationReason',
  header: i18n.global.t('orders.cancellationReason'),
  body: ({ data }: EsShipmentColumnProps) => data.cancellationReason ?? '-',
});

const REQUESTED_MODE_COLUMN = new ColumnSpecification({
  field: 'requestedMode',
  header: i18n.global.t('common.requestedMode'),
  body: ({ data }: EsShipmentColumnProps) =>
    data.requestedMode ? translateEnum(data.requestedMode, 'Mode') : '-',
});

const SOURCE_COLUMN = new ColumnSpecification({
  field: 'source',
  header: i18n.global.t('common.source'),
  body: ({ data }: EsShipmentColumnProps) => data.source ?? '-',
});

const SWC_COLUMN = new ColumnSpecification({
  field: 'swc',
  header: i18n.global.t('swc.shipWithoutContact'),
  body: ({ data }: EsShipmentColumnProps) => renderBoolean(data.swc ?? false),
});

const CUSTOMER_ROUTED_COLUMN = new ColumnSpecification({
  field: 'customerRouted',
  header: i18n.global.t('common.customerRouted'),
  body: ({ data }: EsShipmentColumnProps) =>
    renderBoolean(data.customerRouted ?? false),
});

const MILEAGE_COLUMN = new ColumnSpecification({
  field: 'totalEstimatedMileage',
  header: i18n.global.t('common.mileage'),
  body: ({ data }: EsShipmentColumnProps): string => {
    const total = getTotalMileage(data);
    return total
      ? i18n.global.t('rateShop.mileage', {
          count: Math.round(total),
        })
      : '-';
  },
});

const getTotalMileage = (shipment: ESShipment): number | undefined => {
  return shipment.totalQuotedMileage ?? shipment.totalEstimatedMileage;
};

/**
 * All possible shipment columns
 *
 * Order is significant. This array is used as the default set of columns.
 **/
export const ES_SHIPMENT_COLUMNS: ColumnSpecification[] = [
  OWNING_CLIENT_COLUMN,
  STATUS_HEALTH_COLUMN,
  USER_ASSIGNMENT_COLUMN,
  FINANCIAL_STATUS_COLUMN,
  TAG_COLUMN,
  CARRIER_COLUMN,
  MANIFEST_COLUMN,
  ES_COST_ESTIMATE_COLUMN,
  ORIGIN_COLUMN,
  DESTINATION_COLUMN,
  STOP_COUNT_COLUMN,
  ES_SERVICE_COLUMN,
  MODE_COLUMN,
  ES_REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  SCHEDULED_PICKUP_DATE_RANGE_COLUMN,
  EXPECTED_PICKUP_ARRIVAL_COLUMN,
  ACTUAL_PICKUP_ARRIVAL_COLUMN,
  ACTUAL_PICKUP_DEPARTURE_COLUMN,
  REQUESTED_DELIVERY_DATE_RANGE_COLUMN,
  SCHEDULED_DELIVERY_DATE_RANGE_COLUMN,
  EXPECTED_DELIVERY_ARRIVAL_COLUMN,
  ACTUAL_DELIVERY_ARRIVAL_COLUMN,
  ACTUAL_DELIVERY_DEPARTURE_COLUMN,
  GL_CODE_COLUMN,
  ES_WEIGHT_COLUMN,
  HANDLING_UNIT_COUNT,
  PRODUCT_NUMBER_COLUMN,
  PRODUCT_DESCRIPTION_COLUMN,
  HOT_COLUMN,
  PICKUP_APPOINTMENT_REQUIRED_COLUMN,
  DELIVERY_APPOINTMENT_REQUIRED_COLUMN,
  HAZMAT_COLUMN,
  PAYABLE_COLUMN,
  DIRECTION_COLUMN,
  BILLING_TERMS_COLUMN,
  EQUIPMENT_TYPE_COLUMN,
  FREIGHT_ALLOWANCE_AMOUNT_COLUMN,
  FREIGHT_ALLOWANCE_ENABLED_COLUMN,
  SPOT_MARKET_SUMMARY_COLUMN,
  CANCELLATION_REASON_COLUMN,
  REQUESTED_MODE_COLUMN,
  SOURCE_COLUMN,
  SWC_COLUMN,
  CUSTOMER_ROUTED_COLUMN,
  MILEAGE_COLUMN,
  ES_ORIGIN_LOCATION_GROUP_COLUMN,
  ES_DESTINATION_LOCATION_GROUP_COLUMN,
];

export const ES_SHIPMENT_COLUMNS_SORT_FIELDS = ES_SHIPMENT_COLUMNS.map(
  (col) => col.field,
)
  .filter(notNull)
  .concat('id', '');
